/**
 * FieldShell — the shared row chrome for every dynamic attribute field (PIM
 * Wave P2, Task 3).
 *
 * Renders the visible label (with a requirement marker + a screen-reader
 * requirement note), the widget (`children`), the optional hint, and the
 * inline error. The visible label is a `<span>` and each widget carries its
 * own `aria-label` — mirroring the admin `FormPreview` a11y pattern (a
 * `<label htmlFor>` does not reliably cascade an accessible name onto grouped
 * controls like the boolean Segmented or the size number+unit pair).
 *
 * Requirement markers: `•` (required, `--g-error`) / `◦` (recommended,
 * `--g-warn`) — meaning is never color-only (the sr note + the section legend
 * both spell it out). Optional fields get no marker.
 */
import type { ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";

import { SELLER_DASHBOARD_NS } from "@/features/seller-dashboard/api";
import type {
  FormField,
  Requirement,
} from "@/features/seller-dashboard/attributes/formSchema";

/** Editable input class — mirrors the seller editor's own inputs. */
export const ATTR_INPUT_CLASS =
  "min-h-[44px] w-full rounded-[var(--g-radius)] border border-border bg-card px-3 text-sm text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring";

/** Appended to the input class when the field is in an error state. */
export const ATTR_INPUT_ERROR_CLASS = "border-[var(--g-error)]";

/** Common props every attribute field component accepts. */
export interface AttributeFieldProps {
  field: FormField;
  /** Current locale — drives label/hint/example + option RU-fallback. */
  language: string;
  /** Current form value for this field's primary key (`field_slug`). */
  value: string;
  onChange: (value: string) => void;
  onBlur?: () => void;
  /** i18n message KEY (from RHF / the Zod builder), or undefined when valid. */
  error?: string;
  disabled?: boolean;
}

export function requirementMarker(requirement: Requirement): ReactElement | null {
  if (requirement === "required") {
    return (
      <span aria-hidden="true" className="text-sm leading-none text-[var(--g-error)]">
        •
      </span>
    );
  }
  if (requirement === "recommended") {
    return (
      <span aria-hidden="true" className="text-sm leading-none text-[var(--g-warn)]">
        ◦
      </span>
    );
  }
  return null;
}

export interface FieldShellProps {
  slug: string;
  label: string;
  requirement: Requirement;
  hint?: string;
  /** i18n error message KEY (resolved here), or undefined. */
  error?: string;
  children: ReactNode;
}

export function FieldShell({
  slug,
  label,
  requirement,
  hint,
  error,
  children,
}: FieldShellProps): ReactElement {
  const { t } = useTranslation(SELLER_DASHBOARD_NS);
  const errorId = `attr-${slug}-error`;
  const hintId = `attr-${slug}-hint`;
  const requirementSr =
    requirement === "required" || requirement === "recommended"
      ? t(`attributes.legend.${requirement}`)
      : null;

  return (
    <div className="flex flex-col gap-1.5" data-testid={`attr-field-${slug}`}>
      <span className="flex items-center gap-1.5 text-sm font-medium text-foreground">
        {requirementMarker(requirement)}
        {label}
        {requirementSr ? <span className="sr-only">{`(${requirementSr})`}</span> : null}
      </span>
      {children}
      {hint ? (
        <p id={hintId} className="text-xs text-muted-foreground">
          {hint}
        </p>
      ) : null}
      {error ? (
        <p id={errorId} role="alert" className="text-xs font-medium text-[var(--g-error)]">
          {t(error)}
        </p>
      ) : null}
    </div>
  );
}

export default FieldShell;
